import { writeDraftSnapshot, clearDraftSnapshot, getCurrentSnapshotOrganizationId } from './draftSnapshot';
import { safeStorage } from './safeStorage';

const endpoints = {
  '/clinical/progress-note-drafts': 'ARMHS_PROGRESS_NOTE',
  '/clinical/assessment-drafts': 'DIAGNOSTIC_ASSESSMENT',
  '/clinical/functional-assessment-drafts': 'FUNCTIONAL_ASSESSMENT',
  '/clinical/treatment-plan-drafts': 'ARMHS_TREATMENT_PLAN',
  '/clinical/mh-treatment-plans/draft': 'MH_TREATMENT_PLAN',
  '/clinical/mh-treatment-plans/drafts': 'MH_TREATMENT_PLAN',
  '/clinical/mhis-report-drafts': 'MHIS_REPORT',
  '/mhis-reports/draft': 'MHIS_REPORT',
  '/mhis-reports/drafts': 'MHIS_REPORT',
  '/clinical/release-of-information-drafts': 'RELEASE_OF_INFORMATION',
  '/clinical/armhs-intake-consent-drafts': 'ARMHS_INTAKE_CONSENT',
  '/clinical/outpatient-intake-form-drafts': 'OUTPATIENT_INTAKE_FORM',
  '/clinical/diagnostic-assessment-addendum-drafts': 'DIAGNOSTIC_ASSESSMENT_ADDENDUM',
  '/clinical/columbia-suicide-assessment-drafts': 'COLUMBIA_SUICIDE_ASSESSMENT',
  '/clinical/consent-to-treatment-drafts': 'CONSENT_TO_TREATMENT',
  '/clinical/hipaa-privacy-notice-drafts': 'HIPAA_PRIVACY_NOTICE',
  '/clinical/miscellaneous-document-drafts': 'MISCELLANEOUS_DOCUMENT',
  '/clinical/incident-report-drafts': 'INCIDENT_REPORT',
  '/co-responder/safety-plan-drafts': 'STANLEY_BROWN_SAFETY_PLAN',
  '/co-responder/agency-face-sheet-drafts': 'AGENCY_FACE_SHEET',
};
const revisions = new Map();
const savedIdentities = new Map();
const identityKey = (subject, type, form = {}) => `${owner()}:${subject}:${type}:${form.sessionDate || form.assessmentDate || form.planDate || form.contactDate || ''}`;
export const clinicalDraftIdentity = (subject, type, form) => savedIdentities.get(identityKey(subject, type, form));
const owner = () => {
  const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
  return `${getCurrentSnapshotOrganizationId()}:${user.id || user.userId}`;
};
export const rememberClinicalDraft = (record, saved = false) => {
  const key = `${owner()}:${record?.id}`;
  if (record?.id && (record.lastModified || record.updatedAt) && (saved || !revisions.has(key))) revisions.set(key, record.lastModified || record.updatedAt);
};
export const clinicalDraftRevision = id => revisions.get(`${owner()}:${id}`);
const recordsFrom = body => Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data
  : Array.isArray(body?.drafts) ? body.drafts : [body?.draft || body?.data || body];
const routeFor = path => Object.keys(endpoints).find(base => path === base || (path.startsWith(`${base}/`) && !path.slice(base.length + 1).includes('/')));

// Dedicated form fallbacks use this same protection as the universal service.
// Only the explicit BH draft endpoints are affected; locks, signatures,
// completion, other bundles, and ordinary API requests pass through unchanged.
export function withClinicalDraftPersistence(api) {
  const bindings = new Map();
  const pending = new Set();
  const observedRevisions = new Map();
  const writerId = `api-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const write = async (method, path, body, config) => {
    const base = routeFor(path);
    if (!base || !body || !['formData', 'draftData', 'assessmentData'].some(key => body[key])) return api[method](path, body, config);
    const form = body.formData || body.draftData || body.assessmentData;
    const subject = body.patientId || body.waitlistEntryId;
    const type = body.noteType || body.assessmentType || form.documentType || endpoints[base];
    const key = `${owner()}:${base}:${subject}:${type}:${form.sessionDate || form.assessmentDate || form.planDate || form.contactDate || ''}`;
    const id = body.draftId || (method === 'put' ? path.slice(base.length + 1) : null) || bindings.get(key);
    const receipt = writeDraftSnapshot(subject, type, form, { writerId, draftId: id });
    if (pending.has(key)) throw new Error('A draft save is already in progress. Your device changes are retained.');
    const expected = body.expectedLastModified !== undefined ? body.expectedLastModified
      : id ? observedRevisions.get(`${owner()}:${id}`) || clinicalDraftRevision(id) : null;
    if (id && expected === undefined) throw new Error('Reopen this draft to verify its saved version before saving. Your device copy is retained.');
    pending.add(key);
    try {
      const response = await api[method](path, { ...body, draftId: id || null, expectedLastModified: expected }, config);
      const result = recordsFrom(response.data)[0];
      const record = result && { ...result, id: result.id || response.data.draftId };
      rememberClinicalDraft(record, true);
      if (record?.id) {
        observedRevisions.set(`${owner()}:${record.id}`, record.lastModified || record.updatedAt);
        bindings.set(key, record.id);
        savedIdentities.set(identityKey(subject, type, form), record.id);
      }
      if (receipt && record?.id && (record.lastModified || record.updatedAt)) clearDraftSnapshot(subject, type, receipt);
      return response;
    } finally { pending.delete(key); }
  };
  return {
    ...api,
    get: async (path, config) => {
      const response = await api.get(path, config);
      if (routeFor(path)) recordsFrom(response.data).forEach(record => {
        rememberClinicalDraft(record);
        if (record?.id) observedRevisions.set(`${owner()}:${record.id}`, record.lastModified || record.updatedAt);
      });
      return response;
    },
    post: (path, body, config) => write('post', path, body, config),
    put: (path, body, config) => write('put', path, body, config),
  };
}
